import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * Count UNIQUE attendees in a session.attendance[] array.
 *
 * Why this exists: `attendance[]` is a raw event log (push-per-webhook),
 * so a learner who rejoined Zoom 3 times appears as 3 entries. The UI
 * must show unique-count everywhere, otherwise admins see misleading
 * numbers like "17 present" when only 13 unique learners showed up.
 * V14.5 Part 19p — single source of truth used by SessionModal,
 * SessionDetailModal, and any future surface.
 *
 * Identity key (first non-empty wins, case-insensitive):
 *   email → lms_user_id → user_name → lms_user_name
 * Truly anonymous rows (none of these) fall back to a per-row unique
 * synthetic key so they each count once (not zero, not merged).
 */
export function uniqueAttendanceCount(attendance) {
  if (!Array.isArray(attendance) || attendance.length === 0) return 0;
  const seen = new Set();
  let anonIdx = 0;
  for (const att of attendance) {
    let key = (att?.email || att?.lms_user_id || att?.user_name || att?.lms_user_name || '')
      .toString().toLowerCase().trim();
    if (!key) key = `__anon_${anonIdx++}__`;
    seen.add(key);
  }
  return seen.size;
}


/**
 * Format a Date object to YYYY-MM-DD string in IST timezone.
 * CRITICAL: Never use toISOString().split('T')[0] for date comparisons —
 * that converts to UTC which shifts dates for IST late-night/early-morning sessions.
 */
export function toISTDateStr(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  // IST = UTC + 5:30
  const ist = new Date(d.getTime() + (5.5 * 60 * 60 * 1000) + (d.getTimezoneOffset() * 60 * 1000));
  return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}-${String(ist.getDate()).padStart(2, '0')}`;
}

/**
 * Get today's date in IST as YYYY-MM-DD string
 */
export function todayIST() {
  return toISTDateStr(new Date());
}

export function formatDate(dateStr) {
  if (!dateStr) return '';
  // V14.3 — Parse "YYYY-MM-DD" as LOCAL date (not UTC). Without the
  // "T00:00:00" suffix, `new Date("2026-05-09")` is interpreted as UTC
  // midnight, which renders as May 8 in PST. Adding the local-time suffix
  // anchors it to the user's TZ so the displayed date matches the input.
  // For full ISO timestamps with a "T" component, fall through to default
  // parsing.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = new Date(isDateOnly ? `${dateStr}T00:00:00` : dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// V14.3 — Date + time in IST (or user's timezone). Used wherever the audit
// trail matters (Joined, last login, etc.) — date alone hides whether two
// events happened seconds or hours apart.
export function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// V14.6 — Force IST display (Asia/Kolkata) regardless of the viewer's browser
// timezone. Used on internal admin/ops pages (Storage pipeline jobs, etc.) where
// the Soul Sanskar team is India-based and expects IST. Backend stores UTC and
// serialises tz-aware ISO strings (…+00:00), so conversion is exact.
export function formatIST(dateStr) {
  if (!dateStr) return '-';
  let s = dateStr;
  // Backend stores UTC but older rows serialise a NAIVE ISO string (no 'Z',
  // no ±offset). JS would then parse it as the viewer's local time and show the
  // raw UTC clock mislabelled. Append 'Z' so it's interpreted as UTC, then we
  // convert to IST below.
  if (typeof s === 'string' && s.includes('T') && !/[zZ]$/.test(s) && !/[+-]\d{2}:?\d{2}$/.test(s)) {
    s = s + 'Z';
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }) + ' IST';
}

export function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

export function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch { return 'Asia/Kolkata'; }
}

export function getTimezoneAbbr() {
  const tz = getUserTimezone();
  if (tz.includes('Kolkata') || tz.includes('Calcutta')) return 'IST';
  try {
    const abbr = new Date().toLocaleString('en', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop();
    return abbr;
  } catch { return 'IST'; }
}

export function isIST() {
  const tz = getUserTimezone();
  return tz.includes('Kolkata') || tz.includes('Calcutta') || tz.includes('Asia/Colombo');
}

export function convertISTToLocal(timeStr, dateStr) {
  if (!timeStr || isIST()) return timeStr;
  try {
    const [h, m] = timeStr.split(':').map(Number);
    // Create a date in IST (UTC+5:30)
    const dateToUse = dateStr || new Date().toISOString().split('T')[0];
    const istDate = new Date(`${dateToUse}T${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00+05:30`);
    // Format in user's local timezone
    const localH = istDate.getHours();
    const localM = istDate.getMinutes();
    return `${localH.toString().padStart(2, '0')}:${localM.toString().padStart(2, '0')}`;
  } catch { return timeStr; }
}

export function formatTimeLocal(timeStr, dateStr) {
  const localTime = convertISTToLocal(timeStr, dateStr);
  return formatTime(localTime);
}

// V14.3 — IST date+time → LEARNER's local-TZ date display.
// Used for public-join page + any learner-facing card that combines a
// scheduled date with a scheduled time. Without this, a class on
// "2026-05-09 06:00 IST" displays as "9 May 2026" to a PST learner — but
// for them, that class is actually on "8 May 2026" (5:30 PM PST).
function _istDateTimeToDate(dateStr, timeStr) {
  if (!dateStr) return null;
  const [h, m] = (timeStr || '00:00').split(':').map(Number);
  const hh = (Number.isFinite(h) ? h : 0).toString().padStart(2, '0');
  const mm = (Number.isFinite(m) ? m : 0).toString().padStart(2, '0');
  return new Date(`${dateStr}T${hh}:${mm}:00+05:30`);
}

export function formatLocalDate(dateStr, timeStr) {
  if (!dateStr) return '';
  // If learner is in IST, this is a no-op equivalent to formatDate.
  if (isIST() || !timeStr) return formatDate(dateStr);
  const d = _istDateTimeToDate(dateStr, timeStr);
  if (!d || isNaN(d.getTime())) return formatDate(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// IST date+time → {day, month} for the small calendar badge on session cards.
export function getLocalDayMonth(dateStr, timeStr) {
  if (!dateStr) return { day: '', month: '' };
  const d = (isIST() || !timeStr)
    ? new Date(`${dateStr}T00:00:00`)
    : _istDateTimeToDate(dateStr, timeStr);
  if (!d || isNaN(d.getTime())) return { day: '', month: '' };
  return {
    day: d.getDate(),
    month: d.toLocaleDateString('en', { month: 'short' }).toUpperCase(),
  };
}

// V14.5 Part 11 — IST date+time → "YYYY-MM-DD" key in the user's LOCAL TZ.
// Used for grouping/sorting/Today-checks so non-IST learners see classes
// bucketed by their own calendar day. Falls back to the IST date string
// for IST learners (no-op) and on parse failures.
export function toLocalDateKey(dateStr, timeStr) {
  if (!dateStr) return '';
  if (isIST() || !timeStr) return dateStr;
  const d = _istDateTimeToDate(dateStr, timeStr);
  if (!d || isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// V14.5 Part 11 — IST date+time → short weekday name in user's LOCAL TZ.
// (e.g. for a CDT learner viewing IST May 11 8:00 AM, returns "Sun" because
// it's May 10 9:30 PM CDT.)
export function getLocalWeekday(dateStr, timeStr, { long = false } = {}) {
  if (!dateStr) return '';
  const d = (isIST() || !timeStr)
    ? new Date(`${dateStr}T00:00:00`)
    : _istDateTimeToDate(dateStr, timeStr);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en', { weekday: long ? 'long' : 'short' });
}

// V14.5 Part 11 — Today (YYYY-MM-DD) in the user's LOCAL TZ.
export function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// V14.5 Part 11 — Long-form local weekday + date heading: "Sunday, May 10".
export function formatLocalWeekdayLong(dateStr, timeStr) {
  if (!dateStr) return '';
  const d = (isIST() || !timeStr)
    ? new Date(`${dateStr}T00:00:00`)
    : _istDateTimeToDate(dateStr, timeStr);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function getDayName(dayIndex) {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayIndex];
}

export function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

export function getStatusColor(status) {
  const colors = {
    upcoming: 'bg-blue-50 text-blue-800 border-blue-200',
    live: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    completed: 'bg-slate-50 text-slate-700 border-slate-200',
    canceled: 'bg-rose-50 text-rose-800 border-rose-200',
    missed: 'bg-amber-50 text-amber-800 border-amber-200',
  };
  return colors[status] || 'bg-gray-50 text-gray-700 border-gray-200';
}

export function getBatchTypeLabel(type) {
  return type === 'one_on_one' ? '1:1' : 'Group';
}

export function getCourseTypeLabel(type) {
  const labels = { prenatal: 'Prenatal', postnatal: 'Postnatal', ttc: 'TTC' };
  return labels[type] || type;
}

export function getDurationLabel(mins) {
  if (mins === 30) return '30 min';
  if (mins === 45) return '45 min';
  if (mins === 60) return '1 hour';
  if (mins === 90) return '1hr 30min';
  return `${mins} min`;
}
